import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import SessionTimeoutError from '../modules/SessionTimeoutError'
import * as apiActions from './api'
import * as appActions from './appActions'
import * as modalActions from './modal'
import * as userActions from './user'

// Action types
export const GET_FORM_SUBMISSIONS = 'GET_FORM_SUBMISSIONS'
export const GET_FORM_SUBMISSIONS_SUCCESS = 'GET_FORM_SUBMISSIONS_SUCCESS'
export const GET_FORM_SUBMISSION = 'GET_FORM_SUBMISSION'
export const GET_FORM_SUBMISSION_SUCCESS = 'GET_FORM_SUBMISSION_SUCCESS'
export const UPDATE_SUBMISSION_STATUS = 'UPDATE_SUBMISSION_STATUS'
export const UPDATE_SUBMISSION_STATUS_SUCCESS =
  'UPDATE_SUBMISSION_STATUS_SUCCESS'
export const ADD_SUBMISSION_COMMENT = 'ADD_SUBMISSION_COMMENT'
export const ADD_SUBMISSION_COMMENT_SUCCESS = 'ADD_SUBMISSION_COMMENT_SUCCESS'
export const UPDATE_SUBMISSION_COMMENT = 'UPDATE_SUBMISSION_COMMENT'
export const UPDATE_SUBMISSION_COMMENT_SUCCESS =
  'UPDATE_SUBMISSION_COMMENT_SUCCESS'
export const CLEAR_CURRENT_SUBMISSION = 'CLEAR_CURRENT_SUBMISSION'
export const DELETE_SUBMISSION_FOR_USER = 'DELETE_SUBMISSION_FOR_USER'
export const DELETE_SUBMISSION_FOR_USER_SUCCESS =
  'DELETE_SUBMISSION_FOR_USER_SUCCESS'
export const DELETE_SUBMISSION_FOR_ALL = 'DELETE_SUBMISSION_FOR_ALL'
export const DELETE_SUBMISSION_FOR_ALL_SUCCESS =
  'DELETE_SUBMISSION_FOR_ALL_SUCCESS'
export const MARK_SUBMISSION_VIEWED = 'MARK_SUBMISSION_VIEWED'
export const MARK_SUBMISSION_UNREAD = 'MARK_SUBMISSION_UNREAD'
export const MARK_SUBMISSION_UNREAD_SUCCESS = 'MARK_SUBMISSION_UNREAD_SUCCESS'

/**
 * Action creator to fetch all form submissions for the current user
 */
export const getFormSubmissions = (showSpinner = true) => ({
  type: GET_FORM_SUBMISSIONS,
  showSpinner,
})

/**
 * Action creator to fetch a single form submission by ID
 */
export const getFormSubmission = (submissionId, showSpinner = true) => ({
  type: GET_FORM_SUBMISSION,
  submissionId,
  showSpinner,
})

/**
 * Action creator to update the status of a form submission
 */
export const updateSubmissionStatus = (submissionId, newStatus, note = '') => ({
  type: UPDATE_SUBMISSION_STATUS,
  submissionId,
  newStatus,
  note,
})

/**
 * Action creator to add a comment to a form submission
 */
export const addSubmissionComment = (submissionId, text) => ({
  type: ADD_SUBMISSION_COMMENT,
  submissionId,
  text,
})

/**
 * Action creator to update an existing comment on a form submission
 */
export const updateSubmissionComment = (submissionId, commentId, text) => ({
  type: UPDATE_SUBMISSION_COMMENT,
  submissionId,
  commentId,
  text,
})

/**
 * Action creator to clear the current submission from state
 */
export const clearCurrentSubmission = () => ({
  type: CLEAR_CURRENT_SUBMISSION,
})

/**
 * Action creator to delete a form submission for the current user
 * The submission is hidden from this user but remains visible to other recipients
 * until all recipients have deleted it
 */
export const deleteSubmissionForUser = (submissionId) => ({
  type: DELETE_SUBMISSION_FOR_USER,
  submissionId,
})

/**
 * Action creator to delete a form submission for all users (admin-only)
 * Removes the submission from every recipient's inbox
 */
export const deleteSubmissionForAll = (submissionId) => ({
  type: DELETE_SUBMISSION_FOR_ALL,
  submissionId,
})

/**
 * Action creator to optimistically mark a submission as viewed by the current user
 * This updates the local state immediately for responsive UI
 */
export const markSubmissionViewed = (submissionId, userEmail) => ({
  type: MARK_SUBMISSION_VIEWED,
  submissionId,
  userEmail,
})

/**
 * Action creator to mark a submission as unread by the current user
 */
export const markSubmissionUnread = (submissionId, userEmail) => ({
  type: MARK_SUBMISSION_UNREAD,
  submissionId,
  userEmail,
})

// Watchers
export function* watchGetFormSubmissions() {
  yield takeEvery(GET_FORM_SUBMISSIONS, getFormSubmissionsSaga)
}

export function* watchGetFormSubmission() {
  yield takeEvery(GET_FORM_SUBMISSION, getFormSubmissionSaga)
}

export function* watchUpdateSubmissionStatus() {
  yield takeEvery(UPDATE_SUBMISSION_STATUS, updateSubmissionStatusSaga)
}

export function* watchAddSubmissionComment() {
  yield takeEvery(ADD_SUBMISSION_COMMENT, addSubmissionCommentSaga)
}

export function* watchUpdateSubmissionComment() {
  yield takeEvery(UPDATE_SUBMISSION_COMMENT, updateSubmissionCommentSaga)
}

export function* watchDeleteSubmissionForUser() {
  yield takeEvery(DELETE_SUBMISSION_FOR_USER, deleteSubmissionForUserSaga)
}

export function* watchDeleteSubmissionForAll() {
  yield takeEvery(DELETE_SUBMISSION_FOR_ALL, deleteSubmissionForAllSaga)
}

export function* watchMarkSubmissionUnread() {
  yield takeEvery(MARK_SUBMISSION_UNREAD, markSubmissionUnreadSaga)
}

/**
 * Saga to fetch all form submissions for the current user
 */
export function* getFormSubmissionsSaga(action) {
  yield put({
    type: appActions.INITIATE_ASYNC,
    showSpinner: action.showSpinner,
  })
  try {
    const config = yield select((state) => state.config.appConfig)
    const submissions = yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/list',
      init: {
        method: 'POST',
        body: JSON.stringify({}),
      },
    })
    yield put({ type: GET_FORM_SUBMISSIONS_SUCCESS, submissions })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    console.log('Error fetching form submissions:', { error })
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      // yield put({
      //   type: modalActions.SET_MESSAGE,
      //   msg: {
      //     type: 'ERROR',
      //     msg: error.clientMessage || 'Failed to load form submissions',
      //   },
      // })
      // yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to fetch a single form submission by ID
 */
export function* getFormSubmissionSaga(action) {
  yield put({
    type: appActions.INITIATE_ASYNC,
    showSpinner: action.showSpinner,
  })
  try {
    const config = yield select((state) => state.config.appConfig)
    const submission = yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/get',
      init: {
        method: 'POST',
        body: JSON.stringify({ submissionId: action.submissionId }),
      },
    })
    yield put({ type: GET_FORM_SUBMISSION_SUCCESS, submission })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to load form submission',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to update the status of a form submission
 */
export function* updateSubmissionStatusSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    const config = yield select((state) => state.config.appConfig)
    const userName = yield select((state) => state.user.name)
    yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/update-status',
      init: {
        method: 'POST',
        body: JSON.stringify({
          submissionId: action.submissionId,
          newStatus: action.newStatus,
          note: action.note,
        }),
      },
    })
    yield put({
      type: UPDATE_SUBMISSION_STATUS_SUCCESS,
      submissionId: action.submissionId,
      newStatus: action.newStatus,
      statusHistoryEntry: {
        status: action.newStatus,
        changedAt: new Date().toISOString(),
        changedBy: userName,
        note: action.note || '',
      },
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to update status',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to add a comment to a form submission
 */
export function* addSubmissionCommentSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    const config = yield select((state) => state.config.appConfig)
    const comment = yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/add-comment',
      init: {
        method: 'POST',
        body: JSON.stringify({
          submissionId: action.submissionId,
          text: action.text,
        }),
      },
    })
    yield put({
      type: ADD_SUBMISSION_COMMENT_SUCCESS,
      submissionId: action.submissionId,
      comment,
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to add comment',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to update an existing comment on a form submission
 */
export function* updateSubmissionCommentSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    var config = yield select(function (state) {
      return state.config.appConfig
    })
    var comment = yield call(apiActions.secureFetchSaga, {
      url:
        config.baseUrl + '/api/notifications/form-submissions/update-comment',
      init: {
        method: 'POST',
        body: JSON.stringify({
          submissionId: action.submissionId,
          commentId: action.commentId,
          text: action.text,
        }),
      },
    })
    yield put({
      type: UPDATE_SUBMISSION_COMMENT_SUCCESS,
      submissionId: action.submissionId,
      commentId: action.commentId,
      comment: comment,
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to update comment',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to delete a form submission for the current user
 */
export function* deleteSubmissionForUserSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    var config = yield select(function (state) {
      return state.config.appConfig
    })
    yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/delete',
      init: {
        method: 'POST',
        body: JSON.stringify({ submissionId: action.submissionId }),
      },
    })
    yield put({
      type: DELETE_SUBMISSION_FOR_USER_SUCCESS,
      submissionId: action.submissionId,
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to delete submission',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to delete a form submission for all users (admin-only)
 */
export function* deleteSubmissionForAllSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    var config = yield select(function (state) {
      return state.config.appConfig
    })
    yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/delete',
      init: {
        method: 'POST',
        body: JSON.stringify({
          submissionId: action.submissionId,
          forAll: true,
        }),
      },
    })
    yield put({
      type: DELETE_SUBMISSION_FOR_ALL_SUCCESS,
      submissionId: action.submissionId,
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to delete submission',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

/**
 * Saga to mark a form submission as unread for the current user
 */
export function* markSubmissionUnreadSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })
  try {
    var config = yield select(function (state) {
      return state.config.appConfig
    })
    yield call(apiActions.secureFetchSaga, {
      url: config.baseUrl + '/api/notifications/form-submissions/mark-unread',
      init: {
        method: 'POST',
        body: JSON.stringify({ submissionId: action.submissionId }),
      },
    })
    yield put({
      type: MARK_SUBMISSION_UNREAD_SUCCESS,
      submissionId: action.submissionId,
      userEmail: action.userEmail,
    })
    yield put({ type: appActions.COMPLETE_ASYNC })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to mark submission as unread',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

export default all([
  watchGetFormSubmissions(),
  watchGetFormSubmission(),
  watchUpdateSubmissionStatus(),
  watchAddSubmissionComment(),
  watchUpdateSubmissionComment(),
  watchDeleteSubmissionForUser(),
  watchDeleteSubmissionForAll(),
  watchMarkSubmissionUnread(),
])
